import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { InlineLoading } from '@carbon/react';
import { createColumnHelper, ColumnDef } from '@tanstack/react-table';
import { DataTable } from 'components/data-table';
import { format2, formatNumber } from 'utils/number';
import { fetchDevAreaEvals, retryDevAreaEval } from './atoms';
import type { DevAreaEvalDetail } from './types';
import styles from './page.module.scss';

// Format currency helper
const formatCurrency = (value: number): string =>
  formatNumber({
    value,
    minPrecision: 0,
    maxPrecision: 0,
    style: 'currency',
    includeCommas: true,
  });

interface DevAreaEvalsTableProps {
  packageEvaluationId: number;
}

const STATUS_CONFIG = {
  success: { label: 'Success', style: styles['statusButton--green'] },
  pending: { label: 'Pending', style: styles['statusButton--blue'] },
  error: { label: 'Error', style: styles['statusButton--red'] },
} as const;

const columnHelper = createColumnHelper<DevAreaEvalDetail>();

const buildColumns = (
  onRetryPending: (id: number) => void,
  retryingIds: Set<number>
) => [
  columnHelper.accessor('developmentAreaName', {
    id: 'developmentAreaName',
    header: 'Dev Area',
    size: 150,
    cell: (info) => {
      const daId = info.row.original.developmentAreaId;
      return (
        <a
          href={`/development_areas/${daId}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {info.getValue()}
        </a>
      );
    },
  }),
  columnHelper.accessor('developmentAreaEvaluationId', {
    id: 'developmentAreaEvaluationId',
    header: 'DA Eval ID',
    size: 90,
  }),
  columnHelper.accessor('status', {
    id: 'status',
    header: 'Status',
    size: 80,
    cell: (info) => {
      const status = info.getValue();
      const config = STATUS_CONFIG[status];
      const rowId = info.row.original.id;
      const isRetrying = retryingIds.has(rowId);
      const isPending = status === 'pending';

      if (isRetrying) {
        return <InlineLoading description="Retrying..." />;
      }

      return (
        <button
          type="button"
          className={`${styles.statusButton} ${config.style}`}
          title={
            isPending
              ? 'Re-run this dev area eval in synchronous mode'
              : undefined
          }
          disabled={!isPending}
          onClick={isPending ? () => onRetryPending(rowId) : undefined}
        >
          {config.label}
        </button>
      );
    },
  }),
  columnHelper.accessor('errorMessage', {
    id: 'errorMessage',
    header: 'Error',
    size: 200,
    meta: { type: 'text' },
    cell: (info) => {
      const value = info.getValue();
      if (!value) return '--';
      return <span className={styles.errorText}>{value}</span>;
    },
  }),
  columnHelper.accessor('basin', {
    id: 'basin',
    header: 'Basin',
    size: 100,
    meta: { type: 'text' },
    cell: (info) => info.getValue() || '--',
  }),
  columnHelper.accessor('tier', {
    id: 'tier',
    header: 'Tier',
    size: 60,
    meta: { type: 'text' },
    cell: (info) => info.getValue() || '--',
  }),
  columnHelper.accessor('operatorName', {
    id: 'operatorName',
    header: 'Operator',
    size: 120,
    meta: { type: 'text' },
    cell: (info) => info.getValue() || '--',
  }),
  columnHelper.accessor('totalSlots', {
    id: 'totalSlots',
    header: 'Slots',
    size: 60,
    meta: { type: 'integer' },
  }),
  columnHelper.accessor('ltdCount', {
    id: 'ltdCount',
    header: 'LTD',
    size: 60,
    meta: { type: 'integer' },
  }),
  columnHelper.accessor('ducCount', {
    id: 'ducCount',
    header: 'DUC',
    size: 60,
    meta: { type: 'integer' },
  }),
  columnHelper.accessor('permitCount', {
    id: 'permitCount',
    header: 'Permit',
    size: 60,
    meta: { type: 'integer' },
  }),
  columnHelper.accessor('pdpCount', {
    id: 'pdpCount',
    header: 'PDP',
    size: 60,
    meta: { type: 'integer' },
  }),
  columnHelper.accessor('numberOfAcres', {
    id: 'numberOfAcres',
    header: 'Acres',
    size: 80,
    meta: { type: 'decimal' },
    cell: (info) => {
      const value = info.getValue();
      return value !== null ? format2(value) : '--';
    },
  }),
  columnHelper.accessor('royaltyMaxLtdDollarsPerAcre', {
    id: 'royaltyMaxLtdDollarsPerAcre',
    header: 'Max $/A LTD',
    size: 100,
    meta: { type: 'decimal' },
    cell: (info) => {
      const value = info.getValue();
      return value !== null ? formatCurrency(value) : '--';
    },
  }),
  columnHelper.accessor('royaltyMaxDucDollarsPerAcre', {
    id: 'royaltyMaxDucDollarsPerAcre',
    header: 'Max $/A DUC',
    size: 100,
    meta: { type: 'decimal' },
    cell: (info) => {
      const value = info.getValue();
      return value !== null ? formatCurrency(value) : '--';
    },
  }),
  columnHelper.accessor('royaltyMaxPermitDollarsPerAcre', {
    id: 'royaltyMaxPermitDollarsPerAcre',
    header: 'Max $/A Permit',
    size: 110,
    meta: { type: 'decimal' },
    cell: (info) => {
      const value = info.getValue();
      return value !== null ? formatCurrency(value) : '--';
    },
  }),
  columnHelper.accessor('royaltyMaxPdpDollarsPerAcre', {
    id: 'royaltyMaxPdpDollarsPerAcre',
    header: 'Max $/A PDP',
    size: 100,
    meta: { type: 'decimal' },
    cell: (info) => {
      const value = info.getValue();
      return value !== null ? formatCurrency(value) : '--';
    },
  }),
  columnHelper.accessor('createdAt', {
    id: 'createdAt',
    header: 'Created At',
    size: 180,
    meta: { type: 'datetime' },
  }),
  columnHelper.accessor('outputCreatedAt', {
    id: 'outputCreatedAt',
    header: 'Output At',
    size: 180,
    meta: { type: 'datetime' },
  }),
];

export const DevAreaEvalsTable: FC<DevAreaEvalsTableProps> = ({
  packageEvaluationId,
}) => {
  const [data, setData] = useState<DevAreaEvalDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryingIds, setRetryingIds] = useState<Set<number>>(new Set());
  const mountedRef = useRef(true);

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    []
  );

  const loadData = useCallback(() => {
    setLoading(true);
    setError(null);

    fetchDevAreaEvals(packageEvaluationId)
      .then((result) => {
        if (mountedRef.current) setData(result);
      })
      .catch((e) => {
        if (mountedRef.current) {
          setError(
            e instanceof Error
              ? e.message
              : 'Failed to fetch dev area evaluations'
          );
        }
      })
      .finally(() => {
        if (mountedRef.current) setLoading(false);
      });
  }, [packageEvaluationId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRetryPending = useCallback(
    async (pedaeId: number) => {
      setRetryingIds((prev) => new Set(prev).add(pedaeId));

      const result = await retryDevAreaEval(pedaeId);

      setRetryingIds((prev) => {
        const next = new Set(prev);
        next.delete(pedaeId);
        return next;
      });

      if (result.success) {
        loadData();
      } else {
        setError(`Retry failed for ID ${pedaeId}: ${result.error}`);
      }
    },
    [loadData]
  );

  const columns = useMemo(
    () => buildColumns(handleRetryPending, retryingIds),
    [handleRetryPending, retryingIds]
  );

  if (loading) {
    return (
      <div className={styles.expandedRowContent}>
        <InlineLoading description="Loading development area evaluations..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.expandedRowContent}>
        <div className={styles.expandedRowError}>{error}</div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className={styles.expandedRowContent}>
        <div className={styles.expandedRowEmpty}>
          No development area evaluations found.
        </div>
      </div>
    );
  }

  return (
    <div className={styles.expandedRowContent}>
      <DataTable
        data={data}
        columns={columns as ColumnDef<DevAreaEvalDetail>[]}
        getRowId={(row) => String(row.id)}
        persistenceKey={`eval2-dev-area-evals-${packageEvaluationId}`}
        initialTableState={{
          sortColumn: 'developmentAreaName',
          sortDirection: 'asc',
        }}
        config={{
          columnResizing: true,
          columnReordering: false,
          columnPinning: false,
          sorting: true,
          columnVisibility: true,
          globalSearch: true,
          columnFilters: true,
          export: false,
        }}
      />
    </div>
  );
};
